// react core
import { useContext, useState } from "react";

// date handling
import dayjs from "dayjs";

// material design
import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import Container from "@mui/material/Container";
import Typography from "@mui/material/Typography";

// entzy context and services
import configEntzy from "components/config/ConfigEntzy";
import { MainContext } from "components/main/MainContext";
import { TextTag, HeaderMenuTag } from "components/utils/common/CommonTags";
import { ActionLoader } from "components/utils/common/CommonLoaders";
import { BigButton, MoreButton } from "components/utils/common/CommonButtons";
import { UserAvatar } from "components/utils/common/CommonAvatars";

// fonts and icons
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMessageCaptions as iconMessages } from "@fortawesome/pro-thin-svg-icons";
import { faRocketLaunch as iconActvity } from "@fortawesome/pro-thin-svg-icons";
import { faInfoCircle as iconInfo } from "@fortawesome/pro-thin-svg-icons";
import { faCheck as iconRead } from "@fortawesome/pro-thin-svg-icons";
import { faEdit as iconEdit } from "@fortawesome/pro-duotone-svg-icons";
import { faMessages as iconOpenDMs } from "@fortawesome/pro-duotone-svg-icons";
import { faExternalLink as iconOpenUrl } from "@fortawesome/pro-solid-svg-icons";
import { faClose as iconClose } from "@fortawesome/pro-thin-svg-icons";

function MainFeed(props) {
  // const user = props.user;
  const navigate = props.navigate;

  const feeds = {
    messages: {
      id: "messages",
      name: "Messages",
      icon: iconMessages,
    },
    activity: {
      id: "activity",
      name: "Activity",
      icon: iconActvity,
    },
  };
  const feedView = props.feedView ? props.feedView : "messages";

  const mainContext = useContext(MainContext);

  const [loading, setLoading] = useState(false);
  const [loadingId, setLoadingId] = useState(null);
  const [loadingMore, setLoadingMore] = useState(false);

  const handleNavigateEvent = (item, url) => {
    if (url) {
      window.scrollTo({ top: 0, behavior: "smooth" });
      if (!url.startsWith("http")) {
        mainContext.updateViewerTrail({
          menuSourcePage: feedView,
        });
        mainContext.selectMenu("e");
        navigate(url);
        // handleMarkRead(item, true);
      }
    }
  };

  const handleEditing = () => {
    if (feedView === feeds.messages.id) {
      mainContext.updateViewerTrail({
        editingMessageFeed: !mainContext.state.viewer.trail.editingMessageFeed,
      });
    }
    if (feedView === feeds.activity.id) {
      mainContext.updateViewerTrail({
        editingActivityFeed:
          !mainContext.state.viewer.trail.editingActivityFeed,
      });
    }
  };

  const handleMarkRead = async (notification, readStatus) => {
    setLoading(true);
    setLoadingId(notification.NotificationId);
    const response = await mainContext.prepareUpdateNotification({
      notification: notification,
      action: readStatus ? "markread" : "markunread",
    });
    if (!response.alert) {
      mainContext.updateNotification(response);
    }
    setLoading(false);
    setLoadingId(null);
    return response;
  };
  const handleRemove = async (notification) => {
    setLoading(true);
    setLoadingId(notification.NotificationId);
    const response = await mainContext.prepareUpdateNotification({
      notification: notification,
      action: "remove",
    });
    if (!response.alert) {
      mainContext.updateNotification(response);
    }
    setLoading(false);
    setLoadingId(null);
    return response;
  };

  const handleRefresh = async () => {
    const response = await Promise.all([
      mainContext.preparePullNotifications({
        Category: null,
      }),
      // mainContext.preparePullViewerEvents({
      //   nextToken: null,
      // }),
    ]);
    if (response.filter((item) => item.alert).length > 0) {
      mainContext.updateAlert({
        alert: true,
        message:
          "Hmm unable to refresh content on that try. Give it another go or contact us if this problem persists.",
      });
      return true;
    } else {
      mainContext.pullNotifications(response[0]);
      // mainContext.pullViewerEvents(response[1]);
      return true;
    }
  };

  const handlePullMore = async () => {
    setLoadingMore(true);
    const response = await mainContext.preparePullNotifications({
      nextToken: mainContext.state.notifications.data.nextToken,
      more: true,
    });
    if (response.alert) {
      mainContext.updateAlert(response);
    } else {
      mainContext.pullNotifications(response);
    }
    setLoadingMore(false);
  };

  const NotificationList = (notifyProps) => {
    return (
      <Box
        className="box-default"
        sx={{
          px: configEntzy.APP_SPACING_SM,
        }}
      >
        {notifyProps.notifications.map((item, index) => {
          let internalUrl = item.MessageUrl.replace(configEntzy.APP_URL, "")
            .replace(configEntzy.APP_LONG_URL, "")
            .replace(configEntzy.APP_LOCAL_URL, "")
            .replace(configEntzy.APP_LOCAL_CLOUD_URL, "");
          let displayUrl = internalUrl;
          let memberDirectMessage = false;
          if (internalUrl.startsWith("@")) {
            internalUrl = internalUrl.replace("@", "/@/");
            memberDirectMessage = true;
          }
          if (!memberDirectMessage) {
            if (item.MessageCategory.startsWith("message")) {
              internalUrl += "/debate";
            }
            if (item.MessageCategory.startsWith("activity")) {
              if (item.MessageCategory.endsWith("rider")) {
                internalUrl += "/riders";
              } else {
                internalUrl += "/eventuate";
              }
            }
          }

          // let isMessage =
          //   item.MessageCategory && item.MessageCategory.startsWith("message");
          let daysAgo = dayjs().diff(dayjs(item.LastUpdated), "days");
          return (
            <Box
              key={item.NotificationId}
              className="box-default"
              sx={{
                pb: configEntzy.APP_SPACING_MD,
              }}
            >
              <Box
                className="box-default text-left bg-black-t50 shadow-default"
                sx={{
                  minWidth: configEntzy.AVATAR_SIZE_MD,
                  borderRadius: configEntzy.BORDER_SIZE_XL,
                  borderStyle: "solid",
                  borderWidth: item.MessageRead ? "1px" : "1px",
                  borderColor: item.MessageRead ? "#111" : "purple",
                  // backgroundColor: item.MessageRead ? "transparent" : "black",
                  color: item.MessageRead ? "#555" : "#999",
                }}
              >
                {/* ----- title theme and url click ----- */}
                <Box
                  className={
                    item.MessageRead
                      ? "box-default"
                      : "box-default bg-black-t50"
                  }
                  sx={{
                    px: configEntzy.APP_SPACING_MD2X,
                    py: configEntzy.APP_SPACING_MD,
                    // backgroundColor: item.MessageRead ? "transparent" : "red",
                    color: item.MessageRead ? undefined : "orange",
                    borderTopLeftRadius: configEntzy.BORDER_SIZE_XL,
                    borderTopRightRadius: configEntzy.BORDER_SIZE_XL,
                  }}
                >
                  <Box
                    className="box-default action-pointer"
                    onClick={() => {
                      handleNavigateEvent(item, internalUrl);
                    }}
                  >
                    <Typography variant="h6" noWrap={true}>
                      <span>{displayUrl}&nbsp;&nbsp;</span>
                      <span style={{ float: "right" }}>
                        <FontAwesomeIcon icon={iconOpenUrl} />
                      </span>
                    </Typography>
                  </Box>
                </Box>

                {/* ----- section divider ----- */}
                <Box
                  className="box-default"
                  sx={{
                    // pt: configEntzy.APP_SPACING_MD,
                    // pb: configEntzy.APP_SPACING_MD,
                    opacity: 0.5,
                  }}
                >
                  <Divider
                    sx={{
                      backgroundColor: item.MessageRead ? "#111" : "orange",
                    }}
                  />
                </Box>

                {/* ----- loader ----- */}
                {loading && loadingId === item.NotificationId && (
                  <Box className="box-default zero-height text-center">
                    <Box
                      className="box-inline"
                      sx={{
                        pl: configEntzy.APP_SPACING_XL,
                        pr: configEntzy.APP_SPACING_XL,
                        pt: configEntzy.APP_SPACING_MD,
                        pb: configEntzy.APP_SPACING_MD,
                        borderRadius: configEntzy.BORDER_SIZE_XL,
                        // backgroundColor: item.MessageRead ? "#bbb" : "white",
                        opacity: 0.9,
                      }}
                    >
                      <ActionLoader size="md" />
                    </Box>
                  </Box>
                )}

                {/* ----- content window ----- */}
                <Box
                  className={
                    item.MessageRead
                      ? "box-default"
                      : "box-default bg-black-t50"
                  }
                  sx={{
                    p: configEntzy.APP_SPACING_MD2X,
                    // backgroundColor: item.MessageRead
                    //   ? "transparent"
                    //   : undefined,
                    color: item.MessageRead ? undefined : "white",
                    borderBottomLeftRadius: configEntzy.BORDER_SIZE_XL,
                    borderBottomRightRadius: configEntzy.BORDER_SIZE_XL,
                  }}
                >
                  {/* ----- content url click wrapper ----- */}
                  <Box
                    className="box-default action-pointer"
                    onClick={() => {
                      handleNavigateEvent(item, internalUrl);
                    }}
                  >
                    {/* ----- message title ----- */}
                    <Box className="box-default">
                      <Typography
                        variant={item.MessageRead ? "subtitle1" : "subtitle2"}
                        noWrap={true}
                      >
                        {item.MessageTitle}
                        {item.MessageRead && (
                          <em>
                            <span>&nbsp;&nbsp;</span>
                            <span>
                              (read)
                              {/* ({daysAgo} day{daysAgo === 1 ? "" : "s"} ago) */}
                            </span>
                          </em>
                        )}
                      </Typography>
                    </Box>

                    {/* main message content */}
                    <Box className="box-default">
                      <Typography
                        key={index}
                        variant="body2"
                        sx={{
                          whiteSpace: "pre-line",
                          overflowWrap: "break-word",
                          pt: configEntzy.APP_SPACING_MD,
                        }}
                      >
                        {item.MessageBody}
                      </Typography>
                    </Box>

                    {/* footer content */}
                    <Box
                      className="box-default"
                      sx={{
                        pt: configEntzy.APP_SPACING_MD2X,
                      }}
                    >
                      <Typography variant="body1">
                        <em>
                          <span>
                            {daysAgo} day{daysAgo === 1 ? "" : "s"} ago
                          </span>
                          {item.MessageRead && (
                            <span>
                              &nbsp;&nbsp;|&nbsp;&nbsp;Read&nbsp;&nbsp;
                              <span style={{ color: "green" }}>
                                <FontAwesomeIcon icon={iconRead} fixedWidth />
                              </span>
                            </span>
                          )}
                        </em>
                      </Typography>
                      {displayUrl && displayUrl.startsWith("@") && (
                        <Box className="box-default relative zero-height">
                          <Box
                            className="box-abs"
                            sx={{
                              bottom: 0,
                              right: 0,
                            }}
                          >
                            <UserAvatar
                              user={{ name: displayUrl.replace("@", "") }}
                              size="sm"
                              avatarOnly
                            />
                          </Box>
                        </Box>
                      )}
                    </Box>
                    {/* ----- end url click ----- */}
                  </Box>

                  {/* ----- footer controls ----- */}
                  <Box
                    className="box-default text-center"
                    sx={{
                      mt: configEntzy.APP_SPACING_LG,
                      pt: configEntzy.APP_SPACING_MD,
                      pb: configEntzy.APP_SPACING_MD,
                      color: "white",
                      border: item.MessageRead
                        ? configEntzy.BORDER_DASH_DSM
                        : configEntzy.BORDER_DASH_GSM,
                      borderRadius: configEntzy.BORDER_SIZE_XL,
                    }}
                    hidden={
                      (feedView === feeds.messages.id &&
                        !mainContext.state.viewer.trail.editingMessageFeed) ||
                      (feedView === feeds.activity.id &&
                        !mainContext.state.viewer.trail.editingActivityFeed)
                    }
                  >
                    <Box className="box-default">
                      <TextTag
                        size="md"
                        text={
                          <span>
                            &nbsp;&nbsp;Mark{" "}
                            {item.MessageRead ? "Unread" : "Read"}
                            &nbsp;&nbsp;
                          </span>
                        }
                        shadow={false}
                        rounded={true}
                        margin={true}
                        bgColor={
                          item.MessageRead ? "secondary.light" : "success.dark"
                        }
                        color={item.MessageRead ? "primary.main" : "white"}
                        onClick={() => handleMarkRead(item, !item.MessageRead)}
                      />
                      <TextTag
                        size="md"
                        text={<span>&nbsp;&nbsp;Remove&nbsp;&nbsp;</span>}
                        shadow={false}
                        rounded={true}
                        margin={true}
                        bgColor="error.main"
                        onClick={() => handleRemove(item)}
                      />
                    </Box>
                    {/* any info messages */}
                    <Box
                      className="box-default"
                      sx={{
                        pt: configEntzy.APP_SPACING_SM2X,
                        pb: configEntzy.APP_SPACING_XS3X,
                        pl: configEntzy.APP_SPACING_XS3X,
                        pr: configEntzy.APP_SPACING_XS3X,
                      }}
                    >
                      <Typography
                        variant="body1"
                        color={item.MessageRead ? "white" : "primary"}
                        noWrap
                      >
                        <FontAwesomeIcon icon={iconInfo} fixedWidth />
                        <span>
                          <span>&nbsp;&nbsp;</span>
                          <em>
                            <span>
                              This notification will auto expire in&nbsp;
                              {configEntzy.NOTIFICATION_EXPIRY_DAYS -
                                daysAgo}{" "}
                              day
                              {configEntzy.NOTIFICATION_EXPIRY_DAYS -
                                daysAgo ===
                              1
                                ? ""
                                : "s"}
                            </span>
                          </em>
                          <span>&nbsp;&nbsp;</span>
                        </span>
                        <FontAwesomeIcon icon={iconInfo} fixedWidth />
                      </Typography>
                    </Box>
                  </Box>

                  {/* ----- end content window ----- */}
                </Box>
              </Box>
            </Box>
          );
        })}

        {mainContext.state.notifications.data.nextToken && (
          <Box className="box-default" sx={{ mt: configEntzy.APP_SPACING_LG }}>
            <MoreButton
              variant="contained"
              size="small"
              type="button"
              color="bright"
              text="More"
              loading={loadingMore}
              onClick={() =>
                handlePullMore(mainContext.state.notifications.data.nextToken)
              }
            />
          </Box>
        )}
      </Box>
    );
  };

  const NotificationEmptyList = (notifyEmptyProps) => {
    return (
      <Box
        className="box-default bg-black-t50 shadow-default"
        sx={{
          pt: configEntzy.APP_SPACING_HL,
          pb: configEntzy.APP_SPACING_HL,
          pl: configEntzy.APP_SPACING_MD,
          pr: configEntzy.APP_SPACING_MD,
          borderRadius: configEntzy.BORDER_SIZE_LG,
        }}
      >
        <Container maxWidth="xs">
          <Box
            className="box-default"
            hidden={notifyEmptyProps.category !== "messages"}
          >
            {/* <Typography variant="h1" noWrap={true}>
              <FontAwesomeIcon icon={iconMessages} fixedWidth />
            </Typography> */}
            <Typography
              variant="subtitle2"
              noWrap={true}
              // sx={{
              //   mt: configEntzy.APP_SPACING_MD,
              // }}
            >
              No recent messages
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              sx={{
                mt: configEntzy.APP_SPACING_MD,
              }}
            >
              <em>
                New messages will appear here when people contact you directly
                or if you are subscribed to push notifications inside
                eventuators
              </em>
            </Typography>
          </Box>

          <Box
            className="box-default"
            hidden={notifyEmptyProps.category !== "activity"}
          >
            <Typography variant="h1" noWrap={true}>
              <FontAwesomeIcon icon={iconActvity} fixedWidth />
            </Typography>
            <Typography
              variant="subtitle2"
              noWrap={true}
              sx={{
                mt: configEntzy.APP_SPACING_MD,
              }}
            >
              No recent activity
            </Typography>
            <Typography
              variant="subtitle1"
              align="center"
              sx={{
                mt: configEntzy.APP_SPACING_MD,
              }}
            >
              <em>
                Status changes will appear here for eventuators you own or
                follow or if you have tickets in play
              </em>
            </Typography>
          </Box>
        </Container>
      </Box>
    );
  };

  const NavBar = (navProps) => {
    return (
      <Box
        className="box-default"
        sx={{
          borderRadius: configEntzy.BORDER_SIZE_XL,
          mb: configEntzy.APP_SPACING_MD,
        }}
      >
        <Box
          className="box-default"
          sx={{
            borderRadius: configEntzy.BORDER_SIZE_XL,
          }}
        >
          <Box
            className="box-default half-width"
            sx={{
              pr: configEntzy.APP_SPACING_XS,
            }}
          >
            <BigButton
              size="sm"
              icon={iconOpenDMs}
              iconColor="orange"
              text="Open DMs"
              onClick={() => {
                props.menuSelectById("direct-messages");
              }}
            />
          </Box>
          <Box
            className="box-default half-width"
            sx={{
              pl: configEntzy.APP_SPACING_XS,
            }}
          >
            <BigButton
              size="sm"
              icon={
                (feedView === feeds.messages.id &&
                  mainContext.state.viewer.trail.editingMessageFeed) ||
                (feedView === feeds.activity.id &&
                  mainContext.state.viewer.trail.editingActivityFeed)
                  ? iconClose
                  : iconEdit
              }
              iconColor="orange"
              text={
                (feedView === feeds.messages.id &&
                  mainContext.state.viewer.trail.editingMessageFeed) ||
                (feedView === feeds.activity.id &&
                  mainContext.state.viewer.trail.editingActivityFeed)
                  ? navProps.count === 0
                    ? "Feed Empty"
                    : "Close"
                  : "Edit Feed"
              }
              disabled={navProps.count === 0}
              onClick={handleEditing}
            />
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box className="box-default">
      <Box
        className="box-default"
        sx={{
          p: configEntzy.APP_SPACING_MD,
        }}
      >
        {/* --------- feed content --------- */}
        <Box className="box-default">
          <Container maxWidth="lg" disableGutters>
            <HeaderMenuTag
              icon={feeds[feedView].icon}
              text={`${feeds[feedView].name} Feed`}
              handleRefresh={handleRefresh}
            />
            <Box className="box-default">
              {mainContext.state.notifications.hydrated && (
                <Box
                  className="box-default"
                  sx={{ pt: configEntzy.APP_SPACING_MD }}
                >
                  <Box
                    className="box-default"
                    hidden={feedView !== feeds.messages.id}
                  >
                    <NavBar
                      text="Your Message Notifications"
                      count={mainContext.state.notifications.data.messagesCount}
                      position="left"
                    />
                    <Box
                      className="box-default"
                      sx={{
                        borderRadius: configEntzy.BORDER_SIZE_XL,
                      }}
                    >
                      {mainContext.state.notifications.data.messagesCount >
                      0 ? (
                        <NotificationList
                          notifications={
                            mainContext.state.notifications.data.messages
                          }
                        />
                      ) : (
                        <NotificationEmptyList category="messages" />
                      )}
                    </Box>
                  </Box>
                  <Box
                    className="box-default"
                    hidden={feedView !== feeds.activity.id}
                  >
                    <NavBar
                      text="Your Activity Notifications"
                      count={mainContext.state.notifications.data.activityCount}
                      position="right"
                    />
                    <Box
                      className="box-default"
                      sx={{
                        borderRadius: configEntzy.BORDER_SIZE_XL,
                      }}
                    >
                      {mainContext.state.notifications.data.activityCount >
                      0 ? (
                        <NotificationList
                          notifications={
                            mainContext.state.notifications.data.activity
                          }
                        />
                      ) : (
                        <NotificationEmptyList category="activity" />
                      )}
                    </Box>
                  </Box>
                </Box>
              )}
            </Box>
          </Container>
        </Box>
      </Box>
    </Box>
  );
}

export default MainFeed;
